/**
 * ============================================================================
 * CreateRecipeModal.jsx v4.3 - COMPACT HEADER/FOOTER + BUTTON STRATEGY
 * ============================================================================
 * 
 * 🔧 v4.3: COMPACT LAYOUT
 *   - Header: padding 0.75rem 1rem, h2 → 1rem fontSize
 *   - Footer: padding 0.75rem 1rem, Buttons verkleinert
 *   - Primary: 44px (was 52px), fontSize 0.875rem
 *   - Secondary: 38px (was 44px), fontSize 0.82rem
 *   - Icon: 38px (was 44px), Mini: 28px (was 32px)
 *   - addStep: 38px (was 44px)
 *
 * ✅ v4.2: BUTTON STRATEGY + PARALLEL FOOTER
 * ✅ v4.1: HYBRID BILD-SYSTEM
 * ✅ v4.0: Template-Bildauswahl
 * ✅ v3.0: Planivio Rebranding
 * 
 * @version 4.3.0 - Compact Header/Footer
 */

import React, { useState, useEffect } from 'react';
import ImageSelector from './ImageSelector';
import './Createrecipemodal.css';

// ✅ PLANIVIO FARBEN
const PLANIVIO = {
  teal: '#4A90A4',
  green: '#6BA893',
  warm: '#E8976C',
  border: '#B8D4DC',
  borderLight: '#D4E8EC',
  bgLight: '#E8F4F8',
  bgCard: '#FFFFFF',
  text: '#1F2937',
  textMuted: '#6B7280',
  gradient: 'linear-gradient(135deg, #4A90A4 0%, #6BA893 100%)',
  gradientHover: 'linear-gradient(135deg, #3D7A8C 0%, #5A9680 100%)',
  shadow: 'rgba(74, 144, 164, 0.15)',
  shadowHover: 'rgba(74, 144, 164, 0.25)'
};

// ✅ v4.4: BUTTON STRATEGY TOKENS (korrigiert auf Spec-Werte)
const BTN = {
  primaryH: '52px',
  primaryR: '14px',
  primaryPad: '0 28px',
  primaryFs: '0.95rem',
  primaryShadow: '0 4px 16px rgba(74,144,164,0.25)',
  secondaryH: '44px',
  secondaryR: '12px',
  secondaryPad: '0 20px',
  secondaryFs: '0.9rem',
  iconSize: '44px',
  iconMiniSize: '32px',
  iconR: '12px',
  iconMiniR: '8px',
  iconBg: '#F8FAFC',
  iconBorder: '1px solid #E2E8F0',
  iconColor: '#475569',
  fw: 600,
  gap: '12px',
  gapCompact: '8px',
};

// ============================================================================
// MAIN COMPONENT
// ============================================================================
const CreateRecipeModal = ({ onClose, onCreate, initialData = null }) => {
  
  // STATE
  const [step, setStep] = useState(1);
  
  // Basis-Daten
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [imageKey, setImageKey] = useState('');
  const [category, setCategory] = useState('Hauptgericht');
  const [servings, setServings] = useState(4);
  const [prepTime, setPrepTime] = useState(15);
  const [cookTime, setCookTime] = useState(30);
  const [difficulty, setDifficulty] = useState('Mittel');
  const [tags, setTags] = useState([]);
  const [currentTag, setCurrentTag] = useState('');
  
  // Zutaten
  const [ingredients, setIngredients] = useState([]);
  const [currentIngredient, setCurrentIngredient] = useState({
    name: '',
    quantity: '',
    unit: 'g',
    category: 'Sonstiges'
  });
  
  // Anleitungen
  const [instructions, setInstructions] = useState(['']);
  const [errors, setErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  
  const [isFromTemplate, setIsFromTemplate] = useState(false);

  // ============================================
  // TEMPLATE-DATEN LADEN
  // ============================================
  useEffect(() => {
    if (initialData) {
      console.log('📚 Loading template data:', initialData.name);
      
      setName(initialData.name || '');
      setDescription(initialData.description || '');
      setImageKey(initialData.image_url || initialData.image_key || '');
      setCategory(initialData.category || 'Hauptgericht');
      setServings(initialData.servings || 4);
      setPrepTime(initialData.prep_time || initialData.prepTime || 15);
      setCookTime(initialData.cook_time || initialData.cookTime || 0);
      setDifficulty(initialData.difficulty || 'Mittel');
      
      const templateTags = [];
      if (initialData.isQuick) templateTags.push('Schnell');
      if (initialData.isBudget) templateTags.push('Budget');
      if (initialData.isFamily) templateTags.push('Familienfreundlich');
      if (templateTags.length > 0) setTags(templateTags);
      
      if (initialData.ingredients && initialData.ingredients.length > 0) {
        const mappedIngredients = initialData.ingredients.map((ing, idx) => ({
          id: Date.now() + idx,
          name: ing.name || '',
          quantity: ing.quantity || ing.amount || '',
          unit: ing.unit || 'g',
          category: mapIngredientCategory(ing.category) || 'Sonstiges'
        }));
        setIngredients(mappedIngredients);
      }
      
      if (initialData.instructions && initialData.instructions.length > 0) {
        const mappedInstructions = initialData.instructions.map(inst => {
          if (typeof inst === 'string') return inst;
          return inst.instruction || inst.step || '';
        });
        setInstructions(mappedInstructions.length > 0 ? mappedInstructions : ['']);
      }
      
      setIsFromTemplate(true);
      console.log('✅ Template loaded successfully');
    }
  }, [initialData]);

  function mapIngredientCategory(templateCategory) {
    const mapping = {
      'gemüse': 'Obst & Gemüse',
      'obst': 'Obst & Gemüse',
      'fleisch': 'Fleisch & Fisch',
      'fisch': 'Fleisch & Fisch',
      'milch': 'Milchprodukte',
      'käse': 'Milchprodukte',
      'gewürz': 'Gewürze',
      'gewürze': 'Gewürze',
      'backwaren': 'Brot & Backwaren',
      'brot': 'Brot & Backwaren',
      'konserve': 'Konserven',
      'tiefkühl': 'Tiefkühl',
      'sonstiges': 'Sonstiges'
    };
    
    if (!templateCategory) return 'Sonstiges';
    const lower = templateCategory.toLowerCase();
    return mapping[lower] || templateCategory || 'Sonstiges';
  }

  // KATEGORIEN
  const categories = ['Hauptgericht', 'Vorspeise', 'Dessert', 'Snack', 'Getränk', 'Frühstück', 'Salat', 'Suppe', 'Beilage'];
  const difficulties = ['Leicht', 'Mittel', 'Schwer'];
  const units = ['g', 'kg', 'ml', 'l', 'Stück', 'EL', 'TL', 'Prise', 'Bund', 'Zehe', 'Dose'];
  const ingredientCategories = ['Obst & Gemüse', 'Fleisch & Fisch', 'Milchprodukte', 'Brot & Backwaren', 'Gewürze', 'Konserven', 'Tiefkühl', 'Sonstiges'];

  // VALIDATION
  const validateStep = (currentStep) => {
    const newErrors = {};
    if (currentStep === 1) {
      if (!name.trim()) newErrors.name = 'Name ist erforderlich';
      if (!description.trim()) newErrors.description = 'Beschreibung ist erforderlich';
      if (servings < 1) newErrors.servings = 'Mindestens 1 Portion';
    }
    if (currentStep === 2 && ingredients.length === 0) {
      newErrors.ingredients = 'Mindestens eine Zutat erforderlich';
    }
    if (currentStep === 3) {
      const validInstructions = instructions.filter(i => i.trim());
      if (validInstructions.length === 0) newErrors.instructions = 'Mindestens ein Schritt erforderlich';
    }
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  // TAGS
  const handleAddTag = () => {
    if (currentTag.trim() && !tags.includes(currentTag.trim())) {
      setTags([...tags, currentTag.trim()]);
      setCurrentTag('');
    }
  };
  const handleRemoveTag = (tagToRemove) => setTags(tags.filter(t => t !== tagToRemove));

  // ZUTATEN
  const handleAddIngredient = () => {
    if (currentIngredient.name.trim() && currentIngredient.quantity) {
      setIngredients([...ingredients, { ...currentIngredient, id: Date.now() }]);
      setCurrentIngredient({ name: '', quantity: '', unit: 'g', category: 'Sonstiges' });
    }
  };
  const handleRemoveIngredient = (id) => setIngredients(ingredients.filter(i => i.id !== id));

  // ANLEITUNGEN
  const handleInstructionChange = (index, value) => {
    const newInstructions = [...instructions];
    newInstructions[index] = value;
    setInstructions(newInstructions);
  };
  const handleAddInstruction = () => setInstructions([...instructions, '']);
  const handleRemoveInstruction = (index) => {
    if (instructions.length > 1) setInstructions(instructions.filter((_, i) => i !== index));
  };

  // NAVIGATION
  const handleNext = () => { if (validateStep(step)) setStep(step + 1); };
  const handleBack = () => { setStep(step - 1); setErrors({}); };

  // SUBMIT
  const handleSubmit = async () => {
    if (!validateStep(3)) return;
    setIsSubmitting(true);
    try {
      const isUrl = imageKey.startsWith('http') || imageKey.startsWith('/');
      
      const recipeData = {
        name, 
        description, 
        image_url: isUrl ? imageKey : null,
        image_key: isUrl ? null : imageKey,
        category,
        servings: parseInt(servings),
        prep_time: parseInt(prepTime),
        cook_time: parseInt(cookTime),
        difficulty, 
        tags,
        ingredients: ingredients.map(({ id, ...rest }) => rest),
        instructions: instructions.filter(i => i.trim())
      };
      console.log('📝 Erstelle Rezept:', recipeData);
      await onCreate(recipeData);
    } catch (error) {
      console.error('❌ Fehler:', error);
      setErrors({ submit: 'Fehler beim Erstellen: ' + error.message });
    } finally {
      setIsSubmitting(false);
    }
  };

  // ============================================
  // ✅ v4.2: BUTTON STRATEGY INLINE STYLES
  // ============================================
  const btnStyles = {
    // L1 Primary (52px, 14px) — "Weiter" / "Rezept erstellen"
    primary: {
      height: BTN.primaryH,
      padding: BTN.primaryPad,
      background: PLANIVIO.gradient,
      color: 'white',
      border: 'none',
      borderRadius: BTN.primaryR,
      fontSize: BTN.primaryFs,
      fontWeight: BTN.fw,
      cursor: 'pointer',
      boxShadow: BTN.primaryShadow,
      display: 'inline-flex',
      alignItems: 'center',
      justifyContent: 'center',
      gap: '8px',
      lineHeight: 1,
      boxSizing: 'border-box',
      whiteSpace: 'nowrap',
      transition: 'all 0.2s ease',
      flex: '1 1 auto',
      minWidth: 0,
    },
    // L2 Secondary (44px, 12px, Teal border) — "Abbrechen" / "Zurück"
    secondary: {
      height: BTN.secondaryH,
      padding: BTN.secondaryPad,
      background: 'white',
      color: PLANIVIO.teal,
      border: `2px solid ${PLANIVIO.teal}`,
      borderRadius: BTN.secondaryR,
      fontSize: BTN.secondaryFs,
      fontWeight: BTN.fw,
      cursor: 'pointer',
      display: 'inline-flex',
      alignItems: 'center',
      justifyContent: 'center',
      gap: '8px',
      lineHeight: 1,
      boxSizing: 'border-box',
      whiteSpace: 'nowrap',
      transition: 'all 0.2s ease',
      flex: '1 1 auto',
      minWidth: 0,
    },
    // L4 Icon (44px) — "+"-Buttons
    iconBtn: {
      width: BTN.iconSize,
      height: BTN.iconSize,
      padding: 0,
      background: BTN.iconBg,
      border: BTN.iconBorder,
      borderRadius: BTN.iconR,
      fontSize: '1.25rem',
      color: BTN.iconColor,
      cursor: 'pointer',
      display: 'inline-flex',
      alignItems: 'center',
      justifyContent: 'center',
      lineHeight: 1,
      boxSizing: 'border-box',
      transition: 'all 0.15s ease',
      flexShrink: 0,
    },
    // L4 Mini (32px) — Remove-Buttons
    iconMini: {
      width: BTN.iconMiniSize,
      height: BTN.iconMiniSize,
      padding: 0,
      background: 'transparent',
      border: 'none',
      borderRadius: BTN.iconMiniR,
      fontSize: '1rem',
      color: PLANIVIO.textMuted,
      cursor: 'pointer',
      display: 'inline-flex',
      alignItems: 'center',
      justifyContent: 'center',
      lineHeight: 1,
      boxSizing: 'border-box',
      transition: 'all 0.15s ease',
      flexShrink: 0,
      position: 'relative',
    },
    // L2 Secondary Full-Width — "+ Schritt hinzufügen"
    addStep: {
      height: BTN.secondaryH,
      padding: BTN.secondaryPad,
      background: 'white',
      color: PLANIVIO.teal,
      border: `2px solid ${PLANIVIO.teal}`,
      borderRadius: BTN.secondaryR,
      fontSize: BTN.secondaryFs,
      fontWeight: BTN.fw,
      cursor: 'pointer',
      display: 'inline-flex',
      alignItems: 'center',
      justifyContent: 'center',
      gap: '8px',
      lineHeight: 1,
      boxSizing: 'border-box',
      whiteSpace: 'nowrap',
      transition: 'all 0.2s ease',
      width: '100%',
      marginTop: '0.75rem',
    },
  };

  // 🔧 FIX 1+2: Footer-Layout — Parallel-Buttons
  const footerStyle = {
    padding: '0.75rem 1rem',
    borderTop: `2px solid ${PLANIVIO.borderLight}`,
    display: 'flex',
    justifyContent: step > 1 ? 'space-between' : 'flex-end',
    alignItems: 'center',
    gap: BTN.gap,
    flexWrap: 'wrap',
  };

  // Rechte Button-Gruppe (Abbrechen + Weiter/Erstellen) — IMMER parallel
  const rightGroupStyle = {
    display: 'flex',
    gap: BTN.gap,
    alignItems: 'center',
    flexWrap: 'nowrap',  /* 🔧 FIX 1: Nie umbrechen — immer parallel! */
  };

  // ============================================
  // COMPONENT STYLES
  // ============================================
  const styles = {
    templateNotice: {
      display: 'flex',
      alignItems: 'center',
      gap: '0.5rem',
      padding: '0.6rem 1rem',
      background: PLANIVIO.bgLight,
      borderBottom: '2px solid ' + PLANIVIO.border,
      fontSize: '0.8rem',
      color: PLANIVIO.text
    },
    templateNoticeIcon: {
      width: '26px',
      height: '26px',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      background: PLANIVIO.gradient,
      borderRadius: '6px',
      fontSize: '0.85rem'
    },
    templateHint: {
      display: 'flex',
      alignItems: 'center',
      gap: '0.4rem',
      padding: '0.5rem 0.75rem',
      background: PLANIVIO.bgLight,
      border: '1px solid ' + PLANIVIO.border,
      borderRadius: '8px',
      marginBottom: '0.75rem',
      fontSize: '0.8rem',
      color: PLANIVIO.teal,
      fontWeight: 500
    },
    headerTitle: {
      background: PLANIVIO.gradient,
      WebkitBackgroundClip: 'text',
      WebkitTextFillColor: 'transparent',
      backgroundClip: 'text'
    },
    imageLabel: {
      display: 'block',
      marginBottom: '0.4rem',
      fontWeight: 600,
      color: PLANIVIO.text,
      fontSize: '0.82rem'
    }
  };

  // ============================================
  // RENDER
  // ============================================
  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-content" onClick={e => e.stopPropagation()}>
        
        <div className="modal-header" style={{ padding: '0.6rem 1rem' }}>
          <h2 style={{ ...styles.headerTitle, fontSize: '1rem', margin: 0 }}>
            🍳 {isFromTemplate ? 'Rezept aus Vorlage' : 'Neues Rezept erstellen'}
          </h2>
          {/* L4 Mini Close */}
          <button className="modal-close" onClick={onClose} style={{ fontSize: '0.9rem' }}>✕</button>
        </div>
        
        {isFromTemplate && (
          <div style={styles.templateNotice}>
            <span style={styles.templateNoticeIcon}>📚</span>
            <span>
              Vorlage: <strong style={{ color: PLANIVIO.teal }}>{initialData?.name}</strong> — Du kannst alles anpassen!
            </span>
          </div>
        )}
        
        <div className="modal-progress" style={{ padding: '0.5rem 1rem' }}>
          <div className={'progress-step ' + (step >= 1 ? 'active' : '') + ' ' + (step > 1 ? 'completed' : '')}>
            <div className="progress-number">{step > 1 ? '✓' : '1'}</div>
            <div className="progress-label">Basis</div>
          </div>
          <div className={'progress-line ' + (step > 1 ? 'completed' : '')}></div>
          <div className={'progress-step ' + (step >= 2 ? 'active' : '') + ' ' + (step > 2 ? 'completed' : '')}>
            <div className="progress-number">{step > 2 ? '✓' : '2'}</div>
            <div className="progress-label">Zutaten</div>
          </div>
          <div className={'progress-line ' + (step > 2 ? 'completed' : '')}></div>
          <div className={'progress-step ' + (step >= 3 ? 'active' : '')}>
            <div className="progress-number">3</div>
            <div className="progress-label">Anleitung</div>
          </div>
        </div>
        
        <div className="modal-body">
          
          {/* ============== STEP 1: BASIS ============== */}
          {step === 1 && (
            <div className="form-step">
              
              <div className="form-group">
                <label>Name *</label>
                <input 
                  type="text" 
                  value={name} 
                  onChange={e => setName(e.target.value)} 
                  placeholder="z.B. Spaghetti Carbonara" 
                  className={errors.name ? 'error' : ''} 
                />
                {errors.name && <span className="error-message">{errors.name}</span>}
              </div>
              
              <div className="form-group">
                <label>Beschreibung *</label>
                <textarea 
                  value={description} 
                  onChange={e => setDescription(e.target.value)} 
                  placeholder="Kurze Beschreibung..." 
                  rows={3} 
                  className={errors.description ? 'error' : ''} 
                />
                {errors.description && <span className="error-message">{errors.description}</span>}
              </div>
              
              <div className="form-group">
                <label style={styles.imageLabel}>📷 Rezeptbild auswählen</label>
                <ImageSelector
                  value={imageKey}
                  onChange={setImageKey}
                  category={category}
                />
              </div>
              
              <div className="form-row">
                <div className="form-group">
                  <label>Kategorie</label>
                  <select value={category} onChange={e => setCategory(e.target.value)}>
                    {categories.map(cat => <option key={cat} value={cat}>{cat}</option>)}
                  </select>
                </div>
                <div className="form-group">
                  <label>Schwierigkeit</label>
                  <select value={difficulty} onChange={e => setDifficulty(e.target.value)}>
                    {difficulties.map(diff => <option key={diff} value={diff}>{diff}</option>)}
                  </select>
                </div>
              </div>
              
              <div className="form-row">
                <div className="form-group">
                  <label>Portionen</label>
                  <input 
                    type="number" 
                    value={servings} 
                    onChange={e => setServings(e.target.value)} 
                    min="1" 
                  />
                </div>
                <div className="form-group">
                  <label>Vorbereitung (Min)</label>
                  <input 
                    type="number" 
                    value={prepTime} 
                    onChange={e => setPrepTime(e.target.value)} 
                    min="0" 
                  />
                </div>
                <div className="form-group">
                  <label>Kochzeit (Min)</label>
                  <input 
                    type="number" 
                    value={cookTime} 
                    onChange={e => setCookTime(e.target.value)} 
                    min="0" 
                  />
                </div>
              </div>
              
              <div className="form-group">
                <label>Tags</label>
                <div className="tags-input">
                  <input 
                    type="text" 
                    value={currentTag} 
                    onChange={e => setCurrentTag(e.target.value)}
                    onKeyPress={e => e.key === 'Enter' && (e.preventDefault(), handleAddTag())} 
                    placeholder="Tag hinzufügen..." 
                  />
                  {/* 🔧 FIX 5: L4 Icon-Button */}
                  <button 
                    type="button" 
                    onClick={handleAddTag} 
                    style={btnStyles.iconBtn}
                    onMouseEnter={(e) => { e.currentTarget.style.background = PLANIVIO.bgLight; e.currentTarget.style.borderColor = PLANIVIO.teal; }}
                    onMouseLeave={(e) => { e.currentTarget.style.background = BTN.iconBg; e.currentTarget.style.borderColor = '#E2E8F0'; }}
                  >
                    +
                  </button>
                </div>
                <div className="tags-list">
                  {tags.map(tag => (
                    <span key={tag} className="tag">
                      {tag}
                      {/* 🔧 FIX 5: L4 Mini Remove */}
                      <button 
                        onClick={() => handleRemoveTag(tag)} 
                        style={{ ...btnStyles.iconMini, width: '20px', height: '20px', fontSize: '0.8rem' }}
                      >
                        ×
                      </button>
                    </span>
                  ))}
                </div>
              </div>
            </div>
          )}
          
          {/* ============== STEP 2: ZUTATEN ============== */}
          {step === 2 && (
            <div className="form-step">
              {isFromTemplate && ingredients.length > 0 && (
                <div style={styles.templateHint}>
                  ✨ {ingredients.length} Zutaten aus Vorlage geladen. Du kannst sie bearbeiten oder weitere hinzufügen.
                </div>
              )}
              
              <div className="ingredients-input">
                <div className="form-row">
                  <div className="form-group" style={{ flex: 2 }}>
                    <label>Zutat</label>
                    <input 
                      type="text" 
                      value={currentIngredient.name} 
                      onChange={e => setCurrentIngredient({ ...currentIngredient, name: e.target.value })} 
                      placeholder="z.B. Spaghetti" 
                    />
                  </div>
                  <div className="form-group">
                    <label>Menge</label>
                    <input 
                      type="text" 
                      value={currentIngredient.quantity} 
                      onChange={e => setCurrentIngredient({ ...currentIngredient, quantity: e.target.value })} 
                      placeholder="400" 
                    />
                  </div>
                  <div className="form-group">
                    <label>Einheit</label>
                    <select 
                      value={currentIngredient.unit} 
                      onChange={e => setCurrentIngredient({ ...currentIngredient, unit: e.target.value })}
                    >
                      {units.map(unit => <option key={unit} value={unit}>{unit}</option>)}
                    </select>
                  </div>
                  <div className="form-group">
                    <label>Kategorie</label>
                    <select 
                      value={currentIngredient.category} 
                      onChange={e => setCurrentIngredient({ ...currentIngredient, category: e.target.value })}
                    >
                      {ingredientCategories.map(cat => <option key={cat} value={cat}>{cat}</option>)}
                    </select>
                  </div>
                  {/* 🔧 FIX 5: L4 Icon-Button */}
                  <button 
                    type="button" 
                    onClick={handleAddIngredient} 
                    style={{ ...btnStyles.iconBtn, marginTop: '24px' }}
                    onMouseEnter={(e) => { e.currentTarget.style.background = PLANIVIO.bgLight; e.currentTarget.style.borderColor = PLANIVIO.teal; }}
                    onMouseLeave={(e) => { e.currentTarget.style.background = BTN.iconBg; e.currentTarget.style.borderColor = '#E2E8F0'; }}
                  >
                    +
                  </button>
                </div>
              </div>
              
              {errors.ingredients && <span className="error-message">{errors.ingredients}</span>}
              
              <div className="ingredients-list">
                {ingredients.map((ing, index) => (
                  <div key={ing.id} className="ingredient-item">
                    <span className="ingredient-number">{index + 1}</span>
                    <span className="ingredient-text">
                      <strong style={{ color: PLANIVIO.teal }}>{ing.quantity} {ing.unit}</strong> {ing.name}
                      <span className="ingredient-category">{ing.category}</span>
                    </span>
                    {/* 🔧 FIX 5: L4 Mini Remove mit Danger-Hover */}
                    <button 
                      onClick={() => handleRemoveIngredient(ing.id)} 
                      style={btnStyles.iconMini}
                      onMouseEnter={(e) => { e.currentTarget.style.background = '#FEE2E2'; e.currentTarget.style.color = '#EF4444'; }}
                      onMouseLeave={(e) => { e.currentTarget.style.background = 'transparent'; e.currentTarget.style.color = PLANIVIO.textMuted; }}
                    >
                      🗑️
                    </button>
                  </div>
                ))}
              </div>
              
              {ingredients.length === 0 && (
                <div className="empty-state-small">
                  <span style={{ fontSize: '2rem', marginBottom: '0.5rem', display: 'block' }}>🥕</span>
                  Noch keine Zutaten hinzugefügt
                </div>
              )}
            </div>
          )}
          
          {/* ============== STEP 3: ANLEITUNG ============== */}
          {step === 3 && (
            <div className="form-step">
              {isFromTemplate && instructions.filter(i => i.trim()).length > 0 && (
                <div style={styles.templateHint}>
                  ✨ {instructions.filter(i => i.trim()).length} Schritte aus Vorlage geladen. Du kannst sie anpassen.
                </div>
              )}
              
              <div className="instructions-list">
                {instructions.map((instruction, index) => (
                  <div key={index} className="instruction-input">
                    <div className="instruction-number">{index + 1}</div>
                    <textarea 
                      value={instruction} 
                      onChange={e => handleInstructionChange(index, e.target.value)} 
                      placeholder={'Schritt ' + (index + 1) + '...'} 
                      rows={3} 
                    />
                    {instructions.length > 1 && (
                      /* 🔧 FIX 5: L4 Mini Remove mit Danger-Hover */
                      <button 
                        onClick={() => handleRemoveInstruction(index)} 
                        style={btnStyles.iconMini}
                        onMouseEnter={(e) => { e.currentTarget.style.background = '#FEE2E2'; e.currentTarget.style.color = '#EF4444'; }}
                        onMouseLeave={(e) => { e.currentTarget.style.background = 'transparent'; e.currentTarget.style.color = PLANIVIO.textMuted; }}
                      >
                        🗑️
                      </button>
                    )}
                  </div>
                ))}
              </div>
              
              {errors.instructions && <span className="error-message">{errors.instructions}</span>}
              
              {/* 🔧 FIX 6: L2 Secondary Full-Width */}
              <button 
                type="button" 
                onClick={handleAddInstruction} 
                style={btnStyles.addStep}
                onMouseEnter={(e) => { e.currentTarget.style.background = 'rgba(74,144,164,0.08)'; }}
                onMouseLeave={(e) => { e.currentTarget.style.background = 'white'; }}
              >
                + Schritt hinzufügen
              </button>
            </div>
          )}
          
          {errors.submit && <div className="error-message submit-error">{errors.submit}</div>}
        </div>
        
        {/* ============================================
            🔧 FIX 1+2+3+4: MODAL FOOTER — PARALLEL LAYOUT
            ============================================
            Layout:
            Step 1:        |                    [Abbrechen] [Weiter →]
            Step 2+3: [← Zurück]                [Abbrechen] [Weiter/Erstellen]
            
            "Abbrechen" + "Weiter/Erstellen" sind IMMER parallel (flex-wrap: nowrap)
        */}
        <div style={footerStyle}>
          {/* Links: "Zurück" (nur ab Step 2) */}
          {step > 1 && (
            <button 
              onClick={handleBack} 
              style={btnStyles.secondary}
              disabled={isSubmitting}
              onMouseEnter={(e) => { if (!isSubmitting) e.currentTarget.style.background = 'rgba(74,144,164,0.08)'; }}
              onMouseLeave={(e) => { e.currentTarget.style.background = 'white'; }}
            >
              ← Zurück
            </button>
          )}
          
          {/* Rechts: "Abbrechen" + "Weiter/Erstellen" — IMMER parallel! */}
          <div style={rightGroupStyle}>
            <button 
              onClick={onClose} 
              style={btnStyles.secondary}
              disabled={isSubmitting}
              onMouseEnter={(e) => { if (!isSubmitting) e.currentTarget.style.background = 'rgba(74,144,164,0.08)'; }}
              onMouseLeave={(e) => { e.currentTarget.style.background = 'white'; }}
            >
              Abbrechen
            </button>
            {step < 3 ? (
              <button 
                onClick={handleNext} 
                style={btnStyles.primary}
                onMouseEnter={(e) => { e.currentTarget.style.transform = 'translateY(-1px)'; e.currentTarget.style.boxShadow = '0 6px 24px rgba(74,144,164,0.35)'; }}
                onMouseLeave={(e) => { e.currentTarget.style.transform = 'translateY(0)'; e.currentTarget.style.boxShadow = BTN.primaryShadow; }}
              >
                Weiter →
              </button>
            ) : (
              <button 
                onClick={handleSubmit} 
                style={{
                  ...btnStyles.primary,
                  opacity: isSubmitting ? 0.5 : 1,
                  cursor: isSubmitting ? 'not-allowed' : 'pointer',
                }}
                disabled={isSubmitting}
                onMouseEnter={(e) => { if (!isSubmitting) { e.currentTarget.style.transform = 'translateY(-1px)'; e.currentTarget.style.boxShadow = '0 6px 24px rgba(74,144,164,0.35)'; } }}
                onMouseLeave={(e) => { e.currentTarget.style.transform = 'translateY(0)'; e.currentTarget.style.boxShadow = BTN.primaryShadow; }}
              >
                {isSubmitting ? '⏳ Erstelle...' : '✓ Rezept erstellen'}
              </button>
            )}
          </div>
        </div>
        
      </div>
    </div>
  );
};

export default CreateRecipeModal;